/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { ChevronDown, ChevronUp, Guitar } from 'lucide-react';
import { fetchChordFingerings, ChordFingering, ChordPosition } from '../api';

// Your existing interface and helper functions remain the same
interface ChordFingeringSelectorProps {
  chordRoot: string;
  chordType: string;
  onFingeringSelect: (fingering: ChordFingering | null) => void;
  selectedFingering?: ChordFingering | null;
  fretRange?: { start: number; end: number };
  disabled?: boolean;
}

/**
 * Check if a chord fingering is visible within the given fret range.
 * A fingering is visible if all its non-muted positions have frets within the range.
 */
const isFingeringVisible = (
  fingering: ChordFingering,
  fretRange: { start: number; end: number }
): boolean => {
  // Get all fretted positions (non-muted)
  const frettedPositions = fingering.positions.filter(p => !p.isMuted);

  if (frettedPositions.length === 0) return true;

  // Check if all fretted positions are within the fret range
  return frettedPositions.every(p => p.fret >= fretRange.start && p.fret <= fretRange.end);
};

const ChordFingeringSelector: React.FC<ChordFingeringSelectorProps> = ({
  chordRoot,
  chordType,
  onFingeringSelect,
  selectedFingering,
  fretRange,
  disabled = false
}) => {
  const [fingerings, setFingerings] = useState<ChordFingering[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [userCleared, setUserCleared] = useState(false);
  
  function toCamelCasePosition(p: any): ChordPosition {
    return {
      stringIndex: p.StringIndex,
      fret: p.Fret,
      finger: p.Finger,
      isMuted: p.IsMuted,
      isOpen: p.IsOpen,
      note: p.Note
    };
  }

  function toCamelCaseFingering(f: any): ChordFingering {
    return {
      name: f.Name,
      positionString: f.PositionString,
      fingers: f.FingerString,
      startingFret: f.StartingFret,
      barreFret: f.BarreFret,
      positions: f.Positions ? f.Positions.map(toCamelCasePosition) : []
    };
  }

  useEffect(() => {
    const loadFingerings = async () => {
      if (!chordRoot || !chordType) return;

      setLoading(true);
      setUserCleared(false);
      try {
        const response = await fetchChordFingerings(chordRoot, chordType);
        const fingerings = Array.isArray(response)
          ? response.map(toCamelCaseFingering)
          : [];
        setFingerings(fingerings);
      } catch (error) {
        console.error('Error loading fingerings:', error);
        setFingerings([]);
      } finally {
        setLoading(false);
      }
    };

    loadFingerings();
  }, [chordRoot, chordType]);

  // Auto-select the first visible fingering when fingerings load and none is selected
  useEffect(() => {
    // Only auto-select if:
    // 1. Not currently loading
    // 2. We have fingerings
    // 3. No fingering is currently selected
    if (loading || fingerings.length === 0 || selectedFingering !== null || userCleared) {
      return;
    }

    // Filter by fret range visibility
    const visibleFingerings = fretRange
      ? fingerings.filter(f => isFingeringVisible(f, fretRange))
      : fingerings;

    // Auto-select the first visible fingering
    if (visibleFingerings.length > 0) {
      onFingeringSelect(visibleFingerings[0]);
    }
  }, [loading, fingerings, selectedFingering, userCleared, fretRange, onFingeringSelect]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-sm text-muted-foreground">Loading fingerings...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Guitar className="h-4 w-4" />
          Chord Fingerings - {chordRoot}{chordType}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Clear Selection Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setUserCleared(true); onFingeringSelect(null); }}
          disabled={!selectedFingering || disabled}
          className="w-full mb-4"
        >
          Clear Selection
        </Button>
        
        {(() => {
          // Filter fingerings based on fret range visibility
          const visibleFingerings = fretRange
            ? fingerings.filter(f => isFingeringVisible(f, fretRange))
            : fingerings;

          if (visibleFingerings.length === 0) {
            return (
              <div className="text-center text-sm text-muted-foreground py-4">
                {fingerings.length === 0
                  ? 'No fingerings available for this chord.'
                  : 'No fingerings visible in current fret range. Adjust the fret range to see more options.'}
              </div>
            );
          }

          return (
            /* Fingerings Grid */
            <div className="grid gap-3 max-h-80 overflow-y-auto">
              {visibleFingerings.map((fingering, index) => (
              <Card
                key={index}
                className={`transition-colors ${
                  disabled
                    ? 'cursor-not-allowed opacity-60'
                    : selectedFingering === fingering
                      ? 'cursor-pointer bg-primary/10 border-primary'
                      : 'cursor-pointer hover:bg-muted/50'
                }`}
                onClick={disabled ? undefined : () => onFingeringSelect(fingering)}
              >
                <CardContent className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-sm">{fingering.name}</h4>
                    {fingering.startingFret > 1 && (
                      <Badge variant="secondary" className="text-xs">
                        Fret {fingering.startingFret}
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground mb-2">
                    {fingering.positionString}
                  </p>
                  
                  {fingering.barreFret && (
                    <Badge variant="outline" className="text-xs">
                      Barre: Fret {fingering.barreFret}
                    </Badge>
                  )}
                  
                  {selectedFingering === fingering && (
                    <Badge className="mt-2 text-xs">SELECTED</Badge>
                  )}
                </CardContent>
              </Card>
            ))}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
};

export default ChordFingeringSelector;
